<template>
  <div>
    <!-- Donations Table -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Donor Name
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Blood Group
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Donation Type
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Donation Date
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="donation in filteredDonations"
              :key="donation.id"
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ donation.donorName }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="[
                    'px-3 py-1 rounded-full text-xs font-semibold',
                    getBloodGroupColor(donation.bloodGroup)
                  ]"
                >
                  {{ donation.bloodGroup }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ donation.donationType }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ donation.location }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ donation.donationDate }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  class="px-3 py-1 rounded text-xs font-semibold bg-green-100 text-green-800"
                >
                  {{ donation.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-center gap-2 py-4 border-t border-gray-200">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === 1
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          Previous
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          @click="goToPage(page)"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === page
              ? 'bg-blue-500 text-white'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          {{ page }}
        </button>

        <span v-if="totalPages > 7" class="px-2 text-gray-500">...</span>

        <button
          v-if="totalPages > 7"
          @click="goToPage(totalPages)"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === totalPages
              ? 'bg-blue-500 text-white'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          {{ totalPages }}
        </button>

        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === totalPages
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DonationsTab',
  props: {
    searchQuery: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 8,
      donations: [
        {
          id: 1,
          donorName: 'John Doe',
          bloodGroup: 'O+',
          donationType: 'Whole Blood',
          location: 'Nairobi Hospital',
          donationDate: '2024-01-15',
          status: 'Completed'
        },
        {
          id: 2,
          donorName: 'Jane Smith',
          bloodGroup: 'A-',
          donationType: 'Plasma',
          location: 'Mobile Van - Westlands',
          donationDate: '2024-01-14',
          status: 'Completed'
        },
        {
          id: 3,
          donorName: 'Mike Johnson',
          bloodGroup: 'B+',
          donationType: 'Whole Blood',
          location: 'AAR Hospital',
          donationDate: '2024-01-13',
          status: 'Completed'
        },
        {
          id: 4,
          donorName: 'Sarah Wilson',
          bloodGroup: 'AB+',
          donationType: 'Platelets',
          location: 'Mobile Van - Karen',
          donationDate: '2024-01-12',
          status: 'Completed'
        }
      ]
    };
  },
  computed: {
    filteredDonations() {
      let filtered = this.donations;

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          donation =>
            donation.donorName.toLowerCase().includes(query) ||
            donation.bloodGroup.toLowerCase().includes(query) ||
            donation.location.toLowerCase().includes(query)
        );
      }

      return filtered;
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },
  methods: {
    getBloodGroupColor(bloodGroup) {
      const colors = {
        'A+': 'bg-red-100 text-red-700',
        'A-': 'bg-red-100 text-red-700',
        'B+': 'bg-pink-100 text-pink-700',
        'B-': 'bg-pink-100 text-pink-700',
        'AB+': 'bg-purple-100 text-purple-700',
        'AB-': 'bg-purple-100 text-purple-700',
        'O+': 'bg-orange-100 text-orange-700',
        'O-': 'bg-red-100 text-red-700'
      };
      return colors[bloodGroup] || 'bg-gray-100 text-gray-700';
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        // You can add API call here to fetch data for the specific page
      }
    }
  }
};
</script>

<style scoped>
/* Additional custom styles if needed */
</style>